<template>
  <div>
    <b-form-group class="mt-4" label="لطفا یک حساب انتخاب کنید">
      <b-form-select
        v-model="selectedProvider"
        :options="providerOptions"
        text-field="label"
        value-field="value"
        :disabled="isBusy"
      ></b-form-select>
    </b-form-group>

    <b-row
      v-if="selectedProvider"
      class="mb-3 d-flex justify-content-between align-items-center"
    >
      <b-col md="2" sm="6">
        <b-form-group label="نوع تراکنش">
          <b-form-select
            v-model="transactionType"
            :options="transactionTypeOptions"
            :disabled="isBusy"
          ></b-form-select>
        </b-form-group>
      </b-col>
      <b-col md="2" sm="6">
        <b-form-group label="نوع ترنسفر">
          <b-form-select
            v-model="selectedIsShepaTransaction"
            :options="isShepaTransactionOptions"
            :disabled="isBusy"
          ></b-form-select>
        </b-form-group>
      </b-col>
      <b-col md="2" sm="6">
        <b-form-group label="از تاریخ">
          <date-picker clearable v-model="fromDate"></date-picker>
        </b-form-group>
      </b-col>
      <b-col md="2" sm="6">
        <b-form-group label="تا تاریخ">
          <date-picker clearable v-model="toDate"></date-picker>
        </b-form-group>
      </b-col>
      <b-col md="2" sm="6" class="d-flex align-items-end">
        <b-button
          variant="success"
          block
          :disabled="isBusy"
          :class="isBusy ? 'spinner spinner-light spinner-right' : ''"
          @click="getStatements(1)"
          >فیلتر</b-button
        >
      </b-col>
    </b-row>

    <b-table
      v-if="selectedProvider"
      responsive
      :busy="isBusy"
      style="width: 100%"
      striped
      hover
      show-empty
      empty-text="آیتمی جهت نمایش وجود ندارد"
      :items="statements"
      :fields="fields"
    >
      <template v-slot:table-busy>
        <div class="text-center my-4">
          <b-spinner class="align-middle"></b-spinner>
          <strong class="mr-2">در حال دریافت اطلاعات...</strong>
        </div>
      </template>
      <template #cell(trackingNumber)="row">
        <copyable-table-cell
          :value="row.item.trackingNumber"
          :label="row.field.label"
        />
      </template>
      <template #cell(source)="row">
        <copyable-table-cell
          :value="row.item.source"
          :label="row.field.label"
        />
      </template>
      <template #cell(destination)="row">
        <copyable-table-cell
          :value="row.item.destination"
          :label="row.field.label"
        />
      </template>
      <template v-slot:cell(reverseTransferStatus)="row">
        <div v-if="row.item.isReverseTransfer" class="reverse-transfer-status">
          <span class="reverse-transfer-status__text">عودت شده</span>
          <b-button
            v-if="row.item.linkedTransfer"
            :id="getLinkedTransferTooltipId(row.index)"
            aria-label="اطلاعات عودت"
            class="reverse-transfer-status__info-btn"
            size="sm"
            variant="light"
          >
            <b-icon icon="info-circle" font-scale="1"></b-icon>
          </b-button>
          <b-tooltip
            v-if="row.item.linkedTransfer"
            :target="getLinkedTransferTooltipId(row.index)"
            custom-class="linked-transfer-tooltip"
            placement="top"
            boundary="viewport"
            triggers="hover focus"
          >
            <div class="linked-transfer-tooltip__content">
              <div class="linked-transfer-tooltip__title">
                اطلاعات حساب عودتی
              </div>
              <div class="linked-transfer-tooltip__row">
                <span class="linked-transfer-tooltip__label">شناسه ترنسفر</span>
                <span class="linked-transfer-tooltip__value">
                  {{
                    formatLinkedTransferValue(
                      row.item.linkedTransfer.transferId,
                    )
                  }}
                </span>
              </div>
              <div class="linked-transfer-tooltip__row">
                <span class="linked-transfer-tooltip__label">شناسه سفارش</span>
                <span class="linked-transfer-tooltip__value">
                  {{
                    formatLinkedTransferValue(row.item.linkedTransfer.orderId)
                  }}
                </span>
              </div>
              <div class="linked-transfer-tooltip__row">
                <span class="linked-transfer-tooltip__label">
                  شماره پیگیری خارجی
                </span>
                <span class="linked-transfer-tooltip__value">
                  {{
                    formatLinkedTransferValue(
                      row.item.linkedTransfer.externalTrackingNumber,
                    )
                  }}
                </span>
              </div>
            </div>
          </b-tooltip>
        </div>
        <span v-else>{{ emptyValue }}</span>
      </template>
    </b-table>

    <b-pagination
      v-if="selectedProvider && totalRows > pageSize"
      v-model="currentPage"
      class="mt-3"
      :total-rows="totalRows"
      :per-page="pageSize"
      @change="getStatements"
    ></b-pagination>
  </div>
</template>

<script>
import httpHelper from "../helpers/httpHelper";
import CopyableTableCell from "./CopyableTableCell";
import moment from "jalali-moment";
import { EMPTY_VALUE, hasValue } from "../helpers/copyableHelper";

export default {
  name: "NonShepaTransfers",
  components: {
    CopyableTableCell,
  },
  props: {
    gatewayPin: {
      type: [String, Number],
      default: null,
    },
    bankProviders: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      selectedProvider: null,
      transactionType: null,
      transactionTypeOptions: [
        { text: "همه", value: null },
        { text: "واریز", value: "CREDIT" },
        { text: "برداشت", value: "DEBIT" },
      ],
      selectedIsShepaTransaction: null,
      isShepaTransactionOptions: [
        { text: "همه", value: null },
        { text: "شپایی", value: true },
        { text: "غیرشپایی", value: false },
      ],
      statements: [],
      isBusy: false,
      fromDate: null,
      toDate: null,
      currentPage: 1,
      pageSize: 10,
      totalRows: 0,
      emptyValue: EMPTY_VALUE,
      fields: [
        {
          key: "dateTime",
          label: "تاریخ و زمان",
          formatter: (value) => this.formatDateTime(value),
          tdClass: "ltr-dir",
        },
        {
          key: "trackingNumber",
          label: "شماره پیگیری",
          formatter: (value) => value || EMPTY_VALUE,
        },
        {
          key: "amount",
          label: "مبلغ (ریال)",
          formatter: (value) => this.formatNumber(value),
          tdClass: "ltr-dir",
        },
        {
          key: "balance",
          label: "موجودی",
          formatter: (value) => this.formatNumber(value),
          tdClass: "ltr-dir",
        },
        {
          key: "description",
          label: "شرح",
          formatter: (value) => value || EMPTY_VALUE,
        },
        {
          key: "transactionType",
          label: "نوع تراکنش",
          formatter: (value) => this.formatTransactionType(value),
        },
        {
          key: "source",
          label: "مبدا",
          formatter: (value) => value || EMPTY_VALUE,
          tdClass: "ltr-dir",
        },
        {
          key: "destination",
          label: "مقصد",
          formatter: (value) => value || EMPTY_VALUE,
          tdClass: "ltr-dir",
        },
        {
          key: "isShepaTransaction",
          label: "تراکنش شپا",
          formatter: (value) => (value ? "بله" : "خیر"),
        },
        {
          key: "reverseTransferStatus",
          label: "وضعیت عودت",
        },
      ],
    };
  },
  computed: {
    providerOptions() {
      return this.bankProviders.map((provider) => ({
        label: `${provider.label} - (${provider.accountNumber})`,
        value: provider.nickname,
      }));
    },
  },
  watch: {
    selectedProvider() {
      this.getStatements(1);
    },
  },
  methods: {
    formatTransactionType(value) {
      return value === "CREDIT"
        ? "واریز"
        : value === "DEBIT"
        ? "برداشت"
        : EMPTY_VALUE;
    },
    getLinkedTransferTooltipId(index) {
      return `linked-transfer-tooltip-${index}`;
    },
    formatLinkedTransferValue(value) {
      return value || EMPTY_VALUE;
    },
    formatDateTime(value) {
      if (!value) {
        return EMPTY_VALUE;
      }

      const date = moment(value);
      return date.isValid() ? date.format("jYYYY/jMM/jDD | HH:mm:ss") : value;
    },
    getStatements(page = 1) {
      if (!this.selectedProvider) {
        this.statements = [];
        this.totalRows = 0;
        return;
      }
      const STATEMENTS_URL = `~transfer/providers/${encodeURIComponent(
        this.selectedProvider,
      )}/statements`;

      this.isBusy = true;
      this.currentPage = page;

      const url = `${STATEMENTS_URL}?${this.buildQuery(page)}`;

      httpHelper
        .postData(url, {
          api: this.gatewayPin,
        })
        .then((response) => {
          this.statements = response.data || [];
          this.totalRows = response.total || this.statements.length;
          this.currentPage = payload.page || page;
          this.pageSize = payload.pageSize || this.pageSize;
        })
        .finally(() => {
          this.isBusy = false;
        });
    },
    buildQuery(page) {
      const parts = [`page=${page}`, `pageSize=${this.pageSize}`];
      const fromDate = this.parseDateValue(this.fromDate);
      const toDate = this.parseDateValue(this.toDate);

      if (this.hasValue(this.selectedIsShepaTransaction)) {
        parts.push(
          `isShepaTransaction=${encodeURIComponent(
            this.selectedIsShepaTransaction,
          )}`,
        );
      }

      if (this.hasValue(this.selectedProvider)) {
        parts.push(`nickname=${encodeURIComponent(this.selectedProvider)}`);
      }

      if (this.hasValue(this.transactionType)) {
        parts.push(
          `transactionType=${encodeURIComponent(this.transactionType)}`,
        );
      }

      if (fromDate) {
        parts.push(`fromDate=${encodeURIComponent(fromDate)}`);
      }

      if (toDate) {
        parts.push(`toDate=${encodeURIComponent(toDate)}`);
      }

      return parts.join("&");
    },
    hasValue,
    parseDateValue(value) {
      if (!value) {
        return null;
      }

      let date = moment(value, "jYYYY/jMM/jDD", true);
      if (!date.isValid()) {
        date = moment(value, "jYYYY-jMM-jDD", true);
      }
      if (!date.isValid()) {
        date = moment(value);
      }

      return date.isValid() ? date.toISOString() : null;
    },
    formatNumber(value) {
      if (value == null || value === "") {
        return EMPTY_VALUE;
      }

      const numericValue = Number(value);
      return Number.isFinite(numericValue)
        ? numericValue.toLocaleString()
        : String(value);
    },
  },
};
</script>

<style scoped>
.reverse-transfer-status {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
}

.reverse-transfer-status__text {
  color: #1bc5bd;
  font-weight: 600;
}

.reverse-transfer-status__info-btn {
  width: 26px;
  height: 26px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  border-radius: 50%;
  color: #1bc5bd;
  border: 1px solid rgba(27, 197, 189, 0.35);
  background: rgba(27, 197, 189, 0.08);
}
</style>

<style>
.tooltip.linked-transfer-tooltip {
  opacity: 1 !important;
}

.tooltip.linked-transfer-tooltip .tooltip-inner {
  max-width: 320px;
  padding: 0;
  text-align: right;
  background: #ffffff;
  color: #3f4254;
  border: 1px solid #ebedf3;
  border-radius: 0.65rem;
  box-shadow: 0 8px 24px rgba(82, 63, 105, 0.12);
}

.tooltip.linked-transfer-tooltip.bs-tooltip-top .arrow::before,
.tooltip.linked-transfer-tooltip.bs-tooltip-auto[x-placement^="top"]
  .arrow::before {
  border-top-color: #ffffff;
}

.tooltip.linked-transfer-tooltip.bs-tooltip-bottom .arrow::before,
.tooltip.linked-transfer-tooltip.bs-tooltip-auto[x-placement^="bottom"]
  .arrow::before {
  border-bottom-color: #ffffff;
}

.linked-transfer-tooltip__content {
  min-width: 280px;
  padding: 12px 14px;
  direction: rtl;
}

.linked-transfer-tooltip__title {
  margin-bottom: 10px;
  padding-bottom: 8px;
  color: #181c32;
  font-size: 13px;
  font-weight: 700;
  border-bottom: 1px solid #eff2f5;
}

.linked-transfer-tooltip__row {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  padding: 7px 0;
}

.linked-transfer-tooltip__row + .linked-transfer-tooltip__row {
  border-top: 1px dashed #eff2f5;
}

.linked-transfer-tooltip__label {
  flex: 0 0 auto;
  color: #7e8299;
  font-size: 12px;
  white-space: nowrap;
}

.linked-transfer-tooltip__value {
  flex: 1 1 auto;
  direction: ltr;
  color: #181c32;
  font-family: monospace;
  font-size: 12px;
  font-weight: 600;
  text-align: left;
  word-break: break-all;
}
</style>
